import { z } from 'zod/mini'

export const faroSamplingRuleSchema = z.object({
  /**
   * Route prefix to match. Matching is prefix-based: configuring `'/payment'`
   * matches `'/payment'`, `'/payment/checkout'`, and
   * `'/payment/checkout/cart'`. Leading slash is optional.
   */
  route: z.string(),
  /**
   * Sampling rate for matched routes. Must be between 0 (never sample) and 1
   * (always sample).
   */
  samplingRate: z.number(),

  locales: z.optional(z.array(z.string())),
})

export const faroSamplingRulesSchema = z.array(faroSamplingRuleSchema)

export type FaroSamplingRule = z.infer<typeof faroSamplingRuleSchema>

function routeMatchesLocale(
  locale: string,
  locales: string[] | undefined,
): boolean {
  return locales?.includes(locale) || !locales
}

/**
 * Returns true if the route
 * @returns
 */
function ruleMatches(
  rule: FaroSamplingRule,
  { route, locale }: { route: string; locale: string },
): boolean {
  return route === rule.route && routeMatchesLocale(locale, rule.locales)
}

/**
 *
 * If 2 rules match the same route, the one that includes the local wins
 * @returns
 */
function getBestMatch({
  rule1,
  rule2,
}: {
  rule1: FaroSamplingRule | null
  rule2: FaroSamplingRule
}): FaroSamplingRule {
  if (!rule1) {
    return rule2
  }

  if (rule1.locales && rule1.locales.length > 0) {
    return rule1
  }

  return rule2
}

/**
 * Returns the sampling rate for the most-specific rule that matches
 * `routePath`, or `null` when no rule matches.
 *
 * "Most specific" is defined as the rule whose `route` string is longest
 * (more segments → more specific).
 */
export function getSamplingRateForRoute(
  rules: FaroSamplingRule[],
  { route, locale }: { route: string; locale: string },
): number | null {
  let bestMatch: FaroSamplingRule | null = null

  for (const rule of rules) {
    if (ruleMatches(rule, { route, locale })) {
      bestMatch = getBestMatch({ rule1: bestMatch, rule2: rule })
    }
  }

  return bestMatch?.samplingRate ?? null
}
